import { WhmBox, WhmStack } from "@securitize/reactjs-whm";
import { SettingsLoadingPage } from "../loading/SettingsLoadingPage";
import { SettingsSecurityCard } from "./SettingsSecurityCard";
import { useGetSettingsQuery } from "../../../../data/useProfile";
import { SettingsInternalProps } from "./SettingsInternalProps";
import SettingsEmailField from "./SettingsEmailField";
import SettingsPhoneNumberField from "./SettingsPhoneNumberField";
import SettingsDownloadInfoCard from "./SettingsDownloadInfoCard";
import SettingsLanguageField from "./SettingsLanguageField";
import { SettingsDevicesCard } from "./SettingsDevicesCard";
import { ErrorPage } from "../../../shared/ErrorPage";
import SettingsDeleteCard from "./SettingsDeleteCard";
import useUserInfo from "../../../../hooks/useUserInfo";
import { InvestorTypes } from "../../../../data/types/investors";
import React from "react";
import SettingsTitles from "./SettingsTitles";
import { ProfilePermissionTypes } from "../../../../data/dtos/profile-config.dto";
import SettingsRemoveAccessCard from "./SettingsRemoveAccessCard";
import SettingsAccountIDCard from "./SettingsAccountIDCard";

export default function SettingsDesktop({
  isLoading,
  mfa,
  changePassword,
  language,
  permission,
  onChangeLanguage,
  sessions,
  displayableId,
}: SettingsInternalProps) {
  const { investorType, entityName } = useUserInfo();
  const isEntity = investorType === InvestorTypes.ENTITY;
  const isReadOnly = permission === ProfilePermissionTypes.READ_ONLY;
  const shouldShowRemoveAccess = isEntity && isReadOnly;
  const settingsQuery = useGetSettingsQuery();
  // Main UI
  if (settingsQuery.isPending || isLoading) {
    return <SettingsLoadingPage />;
  }

  if (settingsQuery.isError || sessions.isError) {
    return (
      <ErrorPage
        errorCode={
          settingsQuery.isError
            ? settingsQuery.error.correlationId
            : sessions.correlationId
        }
        onRefresh={
          settingsQuery.isError ? settingsQuery.refetch : sessions.onRefresh
        }
      />
    );
  }

  return (
    <WhmStack>
      {isEntity && (
        <WhmStack gap="20px" marginBottom="20px">
          {settingsQuery.data.hasEntityAccount && (
            <SettingsTitles
              investorType={InvestorTypes.ENTITY}
              entityName={entityName}
            />
          )}

          <WhmStack gap="20px" flexDirection="row" alignItems="flex-start">
            <SettingsDownloadInfoCard permission={permission} />
            {shouldShowRemoveAccess ? (
              <SettingsRemoveAccessCard />
            ) : (
              <SettingsDeleteCard permission={permission} />
            )}
          </WhmStack>
        </WhmStack>
      )}

      {settingsQuery.data.hasEntityAccount && (
        <WhmBox sx={{ marginBottom: "20px" }}>
          <SettingsTitles
            investorType={InvestorTypes.INDIVIDUAL}
            fullName={settingsQuery.data.name}
          />
        </WhmBox>
      )}
      <WhmStack gap="20px" flexDirection="row">
        <WhmStack gap="20px" flex="1">
          <SettingsEmailField email={settingsQuery.data.email} />
          <SettingsAccountIDCard displayableId={String(displayableId)} />
          <SettingsSecurityCard
            onMfa={mfa.onMfa}
            hasMfa={mfa.hasMfa}
            changePassword={changePassword}
          />

          {!isEntity && <SettingsDownloadInfoCard permission={permission} />}
          {!isEntity && <SettingsDeleteCard permission={permission} />}
        </WhmStack>

        <WhmStack gap="20px" flex="1">
          <SettingsPhoneNumberField phone={settingsQuery.data.phone} />
          <SettingsDevicesCard
            activeUserSession={sessions.activeUserSession}
            onDeleteSession={sessions.onDeleteSession}
            userSessions={sessions.userSessions}
          />
          <SettingsLanguageField
            currentLanguage={language}
            onChangeLanguage={onChangeLanguage}
          />
        </WhmStack>
      </WhmStack>
    </WhmStack>
  );
}
